import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdAssignment } from "react-icons/md";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { NotifyContext } from "../../context/notifyContext";
import { PaginationType } from "../../Types/APITypes";
import { Exercise } from "../../Types/Courses";
import CourseService from "../../services/api/CourseService";
import Table from "../../UI/Table/Table";
import Button from "../../UI/Button/Button";

export default function ExerciseList() {
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableInfos, setTableInfos] = useState({ pageCount: 1, total: 0 });

  useBreadcrumbs([
    { label: "Tableau de bord", is_link: true, href: "/app/dashboard" },
    {
      label: "Exercices",
      icon: <MdAssignment />,
      is_link: true,
      href: "/app/courses/exercises",
    },
    { label: "Liste", is_link: false },
  ]);

  const fetchData = useCallback(
    async (pagination?: PaginationType) => {
      setLoading(true);
      try {
        const response: any = await CourseService.getExercises({
          page: (pagination?.current_page || 0) + 1,
          per_page: pagination?.per_page || 20,
          search_word: pagination?.search_word,
        });
        const data = response?.data;
        setExercises(data?.exercises || []);
        setTableInfos({
          pageCount: data?.last_page || 1,
          total: data?.total || 0,
        });
      } catch (error: any) {
        notify(
          error?.message || error || "Erreur lors du chargement des exercices",
          "error"
        );
      } finally {
        setLoading(false);
      }
    },
    [notify]
  );

  const handleDelete = async (exercise: Exercise) => {
    if (!window.confirm(`Supprimer l'exercice "${exercise.title}" ?`)) return;
    try {
      await CourseService.deleteExercise(exercise.id);
      notify("Exercice supprimé avec succès");
      fetchData({ current_page: 0, per_page: 20 });
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de la suppression", "error");
    }
  };

  const columns = useMemo(
    () => [
      { Header: "Titre", accessor: "title" },
      {
        Header: "Portée",
        Cell: ({ row }: any) => (row.original.is_global ? "Global" : "Spécifique"),
      },
      {
        Header: "Description",
        Cell: ({ row }: any) => (
          <div className="max-w-[300px] truncate">{row.original.description || "-"}</div>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }: any) => {
          const exercise: Exercise = row.original;
          return (
            <div className="flex gap-2">
              <Button onClick={() => navigate(`/app/courses/exercises/${exercise.id}/details`)}>
                Détails
              </Button>
              <Button
                color="info"
                onClick={() => navigate(`/app/courses/exercises/${exercise.id}/edit`)}
              >
                Éditer
              </Button>
              <Button color="error" onClick={() => handleDelete(exercise)}>
                Supprimer
              </Button>
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button color="primary" onClick={() => navigate("/app/courses/exercises/create")}>
          Nouvel exercice
        </Button>
      </div>
      <Table
        // @ts-ignore
        columns={columns}
        data={exercises}
        fetchData={fetchData}
        loading={loading}
        pageCount={tableInfos.pageCount}
        total={tableInfos.total}
      />
    </div>
  );
}
